import React, { useEffect, useRef, useState } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import * as API from "../../framework/API/api";
import { ApplicationConfigurationKeys, ENTITYNAME, TOAST_TYPES } from "../../framework/constant/constant";
import { utility } from "../../framework/utility/utilityProvider";
import ToastNotification from "../../components/ToastNotification";
import { useTranslation } from "../../locale/useTranslation";

const PAGE_SIZE_OPTIONS = [5, 10, 20];

const DeepArchivePage = () => {

    const lang = useTranslation();

    const [searchText, setSearchText] = useState("");
    const [activeSearch, setActiveSearch] = useState("");
    const [gridData, setGridData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [syncing, setSyncing] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [mediaCategoryLookup, setMediaCategoryLookup] = useState({});
    const [showRestoreDialog, setShowRestoreDialog] = useState(false);
    const [restoreItem, setRestoreItem] = useState(null);
    const [foldersData, setFoldersData] = useState([]);
    const [foldersLoading, setFoldersLoading] = useState(false);
    const [rowMenu, setRowMenu] = useState({
        visible: false,
        x: 0,
        y: 0,
        row: null
    });

    const containerRef = useRef(null);

    useEffect(() => {
        loadMediaCategoryLookup();
        loadFoldersData();
    }, []);

    useEffect(() => {
        if (!activeSearch.trim()) {
            return;
        }
        loadData(currentPage, pageSize, activeSearch);
    }, [currentPage, pageSize]);

    const loadMediaCategoryLookup = async () => {
        try {
            const res = await API.getDataLookup(ENTITYNAME.MediaCategory, { sort: { SID: 1 } });
            const lookupMap = (res?.data || []).reduce((acc, item) => {
                const key = item?.SID ?? item?._id;
                if (key !== undefined && key !== null) {
                    acc[String(key)] = item?.Description || String(key);
                }
                return acc;
            }, {});
            setMediaCategoryLookup(lookupMap);
        } catch (error) {
            console.log("Media category lookup load error:", error);
        }
    };

    const loadFoldersData = async () => {
        setFoldersLoading(true);
        try {
            const res = await API.getDeepArchiveFolders();
            if (res?.success) {
                setFoldersData(res?.data || []);
            } else {
                setFoldersData([]);
            }
        } catch (error) {
            console.log("Deep archive folders load error:", error);
            setFoldersData([]);
        } finally {
            setFoldersLoading(false);
        }
    };

    const loadData = async (page = 1, perPage = pageSize, search = activeSearch) => {
        setLoading(true);
        setRowMenu((prev) => ({ ...prev, visible: false }));
        try {
            const res = await API.searchDeepArchive({
                page,
                perPage,
                searchQuery: search?.trim() || ""
            });

            const rows = res?.data || [];
            setGridData(rows);
            setTotalRecords(res?.pagination?.totalRecords ?? rows.length);
        } catch (error) {
            console.log("Deep archive load error:", error);
            setGridData([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    useEffect(() => {
        const closeMenu = () => {
            setRowMenu((prev) => ({ ...prev, visible: false }));
        };

        window.addEventListener("click", closeMenu);
        window.addEventListener("scroll", closeMenu, true);

        return () => {
            window.removeEventListener("click", closeMenu);
            window.removeEventListener("scroll", closeMenu, true);
        };
    }, []);

    const handleSearch = () => {
        setActiveSearch(searchText);
        setCurrentPage(1);
        loadData(1, pageSize, searchText);
        setRowMenu((prev) => ({ ...prev, visible: false }));
    };

    const handleRestore = () => {
        if (rowMenu.row?.isRestored === true || rowMenu.row?.isRestore === true) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: "Already restored, you cant restore it." });
            setRowMenu((prev) => ({ ...prev, visible: false }));
            return;
        }

        if (rowMenu.row?.filePath) {
            setRestoreItem(rowMenu.row);
            setShowRestoreDialog(true);
        } else {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: "File path not found for selected item." });
        }

        setRowMenu((prev) => ({ ...prev, visible: false }));
    };

    const submitRestore = async () => {
        if (!restoreItem?.filePath) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: "File path not found for selected item." });
            return;
        }

        const res = await API.getData(ENTITYNAME.ApplicationConfiguration, { query: [["Key", "=", ApplicationConfigurationKeys.DeepArchiveFolder], ["Archive", "!=", true]], });
        if (!res?.success || res?.data?.length == 0 || !res?.data[0]?.Value) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: "Invalid", message: "Deep archive folder path not found in application configuration" });
            return;
        }
        const deepArchiveFolderPath = res.data[0].Value;

        // Normalize folder and relative paths to forward slashes and join with '/'
        const normalizeToSlash = (p) => (p || '').replace(/\\/g, '/').replace(/\/\/+/g, '/').replace(/\/+$/g, '');
        const normalizeRelative = (p) => (p || '').replace(/\\/g, '/').replace(/^\/+/, '');
        const fullFilePath = `${normalizeToSlash(deepArchiveFolderPath)}/${normalizeRelative(restoreItem.filePath)}`;

        const payload = {
            filePath: fullFilePath
        };

        const updateLocalRestoreStatus = (isRestored) => {
            setGridData((prev) =>
                (prev || []).map((item) =>
                    item?._id === restoreItem?._id
                        ? { ...item, isRestore: isRestored, isRestored: isRestored }
                        : item
                )
            );
        };

        const saveRestoreStatus = async (isRestored) => {
            try {
                await API.saveData(ENTITYNAME.MediaDeepArchive, {
                    ...restoreItem,
                    isRestored
                });
            } catch (saveError) {
                console.log("Failed to save restore flag:", saveError);
            }
        };

        setRestoring(true);
        try {
            const res = await API.importLongFormWONStarhubDataViaXML(payload);
            if (!res?.success) {
                updateLocalRestoreStatus(false);
                await saveRestoreStatus(false);
                ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: res?.message || "Restore failed." });
                return;
            }

            updateLocalRestoreStatus(true);
            await saveRestoreStatus(true);
            ToastNotification({ type: TOAST_TYPES.SUCCESS, title: lang.success_message_title, message: res?.message || "Restore completed successfully." });
            setShowRestoreDialog(false);
        } catch (error) {
            updateLocalRestoreStatus(false);
            await saveRestoreStatus(false);
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: error?.message || "Restore failed." });
        } finally {
            setRestoring(false);
        }
    };

    const handleSync = async () => {
        if (syncing) {
            return;
        }

        setSyncing(true);
        try {
            const res = await API.syncDeepArchive();
            if (!res?.success) {
                ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: res?.message || "Deep archive sync failed." });
                return;
            }
            ToastNotification({ type: TOAST_TYPES.SUCCESS, title: lang.success_message_title, message: "Deep archive sync done successfully." });
            loadFoldersData();
            if (activeSearch.trim()) {
                loadData(currentPage, pageSize, activeSearch);
            }
        } catch (error) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: error?.message || "Deep archive sync failed." });
        } finally {
            setSyncing(false);
        }
    };

    const hasSearched = activeSearch.trim().length > 0;

    const getMediaCategoryText = (row) => {
        if (row?.mediaCategory?.Description) {
            return row.mediaCategory.Description;
        }

        const sid = row?.mediaCategory?.SID ?? row?.mediaCategory;
        if (sid === undefined || sid === null) {
            return "-";
        }

        return mediaCategoryLookup[String(sid)] || String(sid);
    };

    const openRowMenu = (event, row) => {
        event.stopPropagation();
        const x = event.clientX + 6;
        const y = event.clientY + 6;
        setRowMenu({
            visible: true,
            x,
            y,
            row
        });
    };

    const wrapperStyle = {
        minHeight: "calc(100vh - 110px)",
        width: "100%",
        padding: "0",
        background: "transparent",
        color: "#2a313d",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: (hasSearched || foldersData.length > 0) ? "flex-start" : "center"
    };

    const cardStyle = {
        width: "100%",
        maxWidth: "100%",
        margin: "0",
        background: "#f5f6f9",
        border: "none",
        borderRadius: "6px",
        boxShadow: "none",
        padding: hasSearched ? "0" : "8px"
    };

    const searchWrapStyle = {
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: hasSearched ? "72px" : (foldersData.length > 0 ? "24vh" : "40vh"),
        paddingLeft: hasSearched ? "170px" : "0",
        paddingRight: hasSearched ? "130px" : "0",
        transition: "all 0.25s ease"
    };

    const searchBarStyle = {
        width: "100%",
        maxWidth: hasSearched ? "900px" : "760px",
        display: "flex",
        gap: "10px",
        alignItems: "center"
    };

    const pageTitleStyle = {
        color: "#3a3633",
        fontWeight: 700,
        letterSpacing: "0.2px"
    };

    const primaryButtonStyle = {
        borderRadius: "4px",
        background: "#3a3633",
        color: "#ffffff",
        padding: "0 20px",
        border: "1px solid #3a3633",
        width: "108px",
        height: "44px",
        fontWeight: 600,
        letterSpacing: "0.2px"
    };

    const mutedLabelStyle = { fontSize: "13px", color: "#616975", fontWeight: 500 };

    const secondaryInputStyle = {
        border: "1px solid #cfd5de",
        borderRadius: "4px",
        background: "#ffffff",
        color: "#1f2937"
    };

    const MenuCell = (props) => {
        const row = props.dataItem;
        return (
            <td>
                <button
                    type="button"
                    onClick={(event) => openRowMenu(event, row)}
                    className="btn btn-sm"
                    style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        border: "1px solid #c7ced8",
                        background: "#f3f5f8",
                        color: "#3f4754",
                        fontWeight: 700,
                        padding: 0
                    }}
                >
                    ...
                </button>
            </td>
        );
    };

    const MediaCategoryCell = (props) => {
        return <td>{getMediaCategoryText(props.dataItem)}</td>;
    };

    const RestoreStatusCell = (props) => {
        const row = props.dataItem;
        const isRestored = row?.isRestored === true || row?.isRestore === true;
        return (
            <td>
                <span
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "3px 10px",
                        borderRadius: "999px",
                        fontSize: "12px",
                        fontWeight: 600,
                        letterSpacing: "0.2px",
                        border: isRestored ? "1px solid #9fc5ad" : "1px solid #d7c38f",
                        background: isRestored ? "#edf7f1" : "#fdf7e8",
                        color: isRestored ? "#1f6a3d" : "#7a5710"
                    }}
                >
                    {isRestored ? "Restored" : "Pending"}
                </span>
            </td>
        );
    };

    return (
        <div style={wrapperStyle} ref={containerRef}>
            <div style={cardStyle}>
                <div style={searchWrapStyle}>
                    {hasSearched ? (
                        <div
                            style={{
                                ...pageTitleStyle,
                                fontSize: "22px",
                                position: "absolute",
                                left: "8px"
                            }}
                        >
                            {lang.deep_archive_header}
                        </div>
                    ) : null}

                    <div style={{ width: "100%", maxWidth: hasSearched ? "900px" : "760px" }}>
                        {!hasSearched ? (
                            <div
                                style={{
                                    ...pageTitleStyle,
                                    fontSize: "34px",
                                    width: "calc(100% - 200px)",
                                    textAlign: "center",
                                    marginBottom: "28px",
                                    transform: "translateY(-12px)"
                                }}
                            >
                                {lang.deep_archive_header}
                            </div>
                        ) : null}

                        <div style={searchBarStyle}>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by title..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        handleSearch();
                                    }
                                }}
                                style={{
                                    flex: 1,
                                    height: "44px",
                                    borderRadius: "4px",
                                    border: "1px solid #cfd5de",
                                    padding: "0 16px",
                                    fontSize: "16px",
                                    boxSizing: "border-box"
                                }}
                            />
                            <button
                                type="button"
                                className="btn btn-sm"
                                onClick={handleSearch}
                                style={{
                                    ...primaryButtonStyle,
                                    background: "#3a3633",
                                    borderColor: "#3a3633"
                                }}
                            >
                                {lang.search_button_text}
                            </button>
                            {!hasSearched ? (
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    onClick={handleSync}
                                    disabled={syncing}
                                    style={{
                                        ...primaryButtonStyle,
                                        background: syncing ? "#9099a8" : "#3a3633",
                                        borderColor: syncing ? "#9099a8" : "#3a3633"
                                    }}
                                >
                                    {syncing ? lang.syncing_button_text : lang.sync_button_text}
                                </button>
                            ) : null}
                            {hasSearched ? (
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    onClick={handleSync}
                                    disabled={syncing}
                                    style={{
                                        ...primaryButtonStyle,
                                        background: syncing ? "#9099a8" : "#3a3633",
                                        borderColor: syncing ? "#9099a8" : "#3a3633"
                                    }}
                                >
                                    {syncing ? lang.syncing_button_text : lang.sync_button_text}
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>

                {hasSearched ? (
                    <div style={{ marginTop: "6px", width: "100%" }}>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "8px",
                                gap: "8px",
                                flexWrap: "wrap"
                            }}
                        >
                            <div style={mutedLabelStyle}>
                                Showing {totalRecords} result(s)
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={mutedLabelStyle}>Rows:</span>
                                <select
                                    className="form-control form-control-sm"
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    style={{
                                        width: "72px",
                                        height: "34px",
                                        padding: "4px 8px",
                                        ...secondaryInputStyle
                                    }}
                                >
                                    {PAGE_SIZE_OPTIONS.map((size) => (
                                        <option key={size} value={size}>
                                            {size}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div
                            style={{
                                border: "1px solid #d6dbe3",
                                borderRadius: "6px",
                                overflow: "hidden",
                                position: "relative",
                                background: "#ffffff",
                                maxHeight: "calc(100vh - 240px)"
                            }}
                        >
                            {loading ? (
                                <div style={{ textAlign: "center", padding: "24px" }}>
                                    Loading archive data...
                                </div>
                            ) : (
                                <Grid
                                    style={{ width: "100%", height: "calc(100vh - 240px)" }}
                                    data={gridData}
                                    dataItemKey="_id"
                                    scrollable="scrollable"
                                    resizable={true}
                                >
                                    <GridColumn cell={MenuCell} title="" width="64px" />
                                    <GridColumn field="title" title="Title" minResizableWidth={320} />
                                    <GridColumn field="seasonNumber" title="Season Number" width="170px" />
                                    <GridColumn field="episodeCount" title="Episode Count" width="170px" />
                                    <GridColumn cell={MediaCategoryCell} title="Media Category" width="190px" />
                                    <GridColumn cell={RestoreStatusCell} title="Restore Status" width="170px" />
                                </Grid>
                            )}

                            {!loading && !gridData.length ? (
                                <div style={{ textAlign: "center", padding: "18px", color: "#616975" }}>
                                    No data found.
                                </div>
                            ) : null}

                            {rowMenu.visible ? (
                                <div
                                    style={{
                                        position: "fixed",
                                        top: `${rowMenu.y}px`,
                                        left: `${rowMenu.x}px`,
                                        minWidth: "210px",
                                        background: "#f7f8fb",
                                        border: "1px solid #cfd5de",
                                        borderRadius: "6px",
                                        boxShadow: "none",
                                        padding: "0",
                                        zIndex: 9999
                                    }}
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <div
                                        style={{
                                            fontSize: "11px",
                                            color: "#4b5565",
                                            padding: "8px 10px 6px",
                                            borderBottom: "1px solid #d7dde6",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.7px",
                                            fontWeight: 600
                                        }}
                                    >
                                        Options
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleRestore}
                                        className="btn btn-sm"
                                        style={{
                                            width: "100%",
                                            textAlign: "left",
                                            borderRadius: "0",
                                            border: "none",
                                            background: "transparent",
                                            padding: "9px 10px",
                                            color: "#273142",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            fontWeight: 500
                                        }}
                                    >
                                        <span
                                            style={{
                                                width: "6px",
                                                height: "6px",
                                                borderRadius: "50%",
                                                background: "#5a6473",
                                                display: "inline-block"
                                            }}
                                        />
                                        Restore from Deep Archive
                                    </button>
                                </div>
                            ) : null}
                        </div>

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginTop: "10px",
                                gap: "10px",
                                flexWrap: "wrap"
                            }}
                        >
                            <div style={mutedLabelStyle}>
                                Page {currentPage} of {totalPages}
                            </div>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={currentPage <= 1}
                                    onClick={() => {
                                        const nextPage = Math.max(1, currentPage - 1);
                                        setCurrentPage(nextPage);
                                    }}
                                >
                                    Previous
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => {
                                        const nextPage = Math.min(totalPages, currentPage + 1);
                                        setCurrentPage(nextPage);
                                    }}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                ) : null}

                {!hasSearched ? (
                    <div style={{ marginTop: "24px", width: "100%", display: "flex", justifyContent: "center" }}>
                        <div style={{ width: "100%", maxWidth: "760px", background: "#ffffff", border: "1px solid #d6dbe3", borderRadius: "6px", padding: "20px" }}>
                            <h4 style={{ color: "#3a3633", fontWeight: 600, marginBottom: "16px", borderBottom: "1px solid #eef0f3", paddingBottom: "10px" }}>
                               {lang.media_overview_label}
                            </h4>
                            {foldersLoading ? (
                                <div style={{ color: "#616975", fontStyle: "italic", textAlign: "center", padding: "12px" }}>{lang.loading_folders_label}</div>
                            ) : foldersData.length === 0 ? (
                                <div style={{ color: "#616975", textAlign: "center", padding: "12px" }}>{lang.no_folder_data_available_use_sync_to_pull_data_placeholder}</div>
                            ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                    {foldersData.map((folder, index) => (
                                        <div key={index} style={{ borderBottom: index === foldersData.length - 1 ? "none" : "1px dashed #e5e7eb", paddingBottom: index === foldersData.length - 1 ? "0" : "10px" }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                                                <span style={{ fontWeight: 600, color: "#1f2937", fontSize: "15px" }}>
                                                    {folder.folderName}
                                                </span>
                                                <span style={{ background: "#f3f4f6", color: "#374151", padding: "2px 8px", borderRadius: "999px", fontSize: "12px", fontWeight: 600 }}>
                                                    {folder.fileCount} {folder.fileCount === 1 ? 'file' : 'files'}
                                                </span>
                                            </div>
                                            {/* <div style={{ fontSize: "13px", color: "#6b7280", wordBreak: "break-all" }}>
                                                {folder.absoluteFolderPath}
                                            </div> */}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>

            {showRestoreDialog ? (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(15, 23, 42, 0.22)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 10000
                    }}
                >
                    <div
                        style={{
                            width: "100%",
                            maxWidth: "520px",
                            background: "#ffffff",
                            border: "1px solid #cfd5de",
                            borderRadius: "6px",
                            padding: "16px",
                            boxShadow: "none"
                        }}
                    >
                        <div style={{ fontSize: "17px", fontWeight: 600, marginBottom: "10px", color: "#3a3633" }}>
                           {lang.restore_from_deep_archive_label}
                        </div>
                        <div style={{ fontSize: "13px", color: "#616975", marginBottom: "12px" }}>
                            {lang.title_label}: {restoreItem?.title || "-"}
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => setShowRestoreDialog(false)}
                                disabled={restoring}
                                style={{
                                    borderRadius: "4px",
                                    border: "1px solid #9aa3ae",
                                    color: "#3a3633",
                                    background: "#ffffff",
                                    minWidth: "74px",
                                    height: "38px",
                                    fontWeight: 500
                                }}
                            >
                                {lang.cancel_button_text}
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={submitRestore}
                                disabled={restoring}
                                style={{
                                    borderRadius: "4px",
                                    border: "1px solid #3a3633",
                                    color: "#ffffff",
                                    background: restoring ? "#8f8a86" : "#3a3633",
                                    minWidth: "84px",
                                    height: "38px",
                                    fontWeight: 600
                                }}
                            >
                                {restoring ? lang.restoring_button_text : lang.restore_button_text}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default DeepArchivePage;
